import { memo, useCallback, useLayoutEffect, useMemo } from 'react';
import { LoadingTracker } from 'src/components/common/LoadingTracker';
import { ActivePosEntityProvider } from 'src/contexts/ActivePosEntityContext';
import {
  ActiveSortTableColumnProvider,
  TableViewMode,
} from 'src/contexts/ActiveSortTableColumnContext';
import { AdCampaignsDataContextProvider } from 'src/contexts/AdCampaignsDataContext';
import { useAppContext } from 'src/contexts/AppContext';
import {
  CatalogDataContextProvider,
  ExpandedEventData,
} from 'src/contexts/CatalogDataContext';
import { CatalogMetricsContextProvider } from 'src/contexts/CatalogMetricsContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { FilterQueryContextProvider } from 'src/contexts/FilterQueryContext';
import { useFilterQueryContextFilterQuerySelector } from 'src/contexts/FilterQueryContext/FilterQueryContext.selectors';
import { ListingNotificationContextProvider } from 'src/contexts/ListingNotificationContext';
import { ListingStatusContextProvider } from 'src/contexts/ListingStatusContext';
import { SidePanelProvider } from 'src/contexts/SidePanelContext';
import { StopwatchLoggingContextProvider } from 'src/contexts/StopwatchLoggingContext';
import { useGetActiveAccountClientConfig } from 'src/hooks/useGetActiveAccountClientConfig';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { usePageLoadStatus } from 'src/hooks/usePageLoadStatus';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { EVENT_DETAILED_METRICS_QUERY_KEY } from 'src/tables/EventsTable/EventsTable.constants';
import { EventsTableColumnId } from 'src/utils/columns/events/eventsColumnUtils.types';
import { DefaultListingTableSorting } from 'src/utils/constants/tableColumns';
import {
  DefaultListingQuery,
  EmptyListingQuery,
} from 'src/utils/eventQueryUtils';
import { transformData } from 'src/utils/eventWithDataUtils';
import {
  ListingCatalogMetricsQueryKey,
  ListingCatalogQueryKey,
} from 'src/utils/inventoryUtils';
import { addListingMetrics } from 'src/utils/ticketMetricUtils';
import {
  ActionOutboxEntityType,
  Feature,
  InventoryViewMode,
  ListingClient,
  ListingDetailDataField,
  ListingDetails,
  ListingMetrics,
  ListingQuery,
  UserSetting,
} from 'src/WebApiController';

import {
  getCatalogMetrics,
  getEventDetailedMetrics,
  getListingsSectionalDataForEvents,
  listingQueryValueTransformFromUrl,
  listingQueryValueTransformToUrl,
  minimizeListingQueryForMetrics,
} from './Inventory.utils';
import { InventoryEventsExplorer } from './InventoryEventsExplorer';
import { InventoryEventsMobile } from './mobile';

function InventoryRaw() {
  const { trackError } = useClientLogContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const { registerComponent } = usePageLoadStatus();

  const hasUnsoldTicketsFilterFeature = useUserHasFeature(
    Feature.UnsoldTicketsFilter
  );

  useLayoutEffect(() => {
    registerComponent('InventoryEventsExplorer');
  }, [registerComponent]);

  const getCatalogDataExpandedCallback = useCallback(
    (ids: string[], filterQuery: ListingQuery) => {
      return getListingsSectionalDataForEvents(
        ids,
        filterQuery,
        [
          ListingDetailDataField.Basic,
          ListingDetailDataField.Pricing,
          ListingDetailDataField.AutoPricing,
          ListingDetailDataField.InternalNotes,
        ],
        {
          activeAccountWebClientConfig,
          onError: (error) => {
            trackError(
              'ListingClient.getListingsSectionalDataForEvents',
              error,
              {
                ...filterQuery,
                eventIds: ids,
              }
            );
          },
        }
      );
    },
    [activeAccountWebClientConfig, trackError]
  );

  const onCatalogDataExpandedCallback = useCallback(
    (
      ids: string[],
      filterQuery: ListingQuery,
      currentData?: ExpandedEventData
    ) =>
      getListingsSectionalDataForEvents(
        ids,
        filterQuery,
        [
          ListingDetailDataField.Purchase,
          ListingDetailDataField.Tags,
          ListingDetailDataField.InternalNotes,
        ],
        {
          activeAccountWebClientConfig,
          onError: (error) => {
            trackError(
              'ListingClient.getListingsSectionalDataForEvents',
              error,
              {
                ...filterQuery,
                eventIds: ids,
              }
            );
          },
        },
        currentData
      ),
    [activeAccountWebClientConfig, trackError]
  );

  return (
    <StopwatchLoggingContextProvider>
      <FilterQueryContextProvider<ListingQuery>
        initialQuery={
          {
            ...DefaultListingQuery,
            hasUnsoldTickets: hasUnsoldTicketsFilterFeature ? true : null,
          } as ListingQuery
        }
        emptyQuery={EmptyListingQuery}
        viewModeSettingId={UserSetting.InventoryPageViewMode}
        queryValueTransformToUrl={listingQueryValueTransformToUrl}
        queryValueTransformFromUrl={listingQueryValueTransformFromUrl}
        saveQueryInUrl
      >
        <CatalogDataContextProvider<ListingQuery>
          entityType={ActionOutboxEntityType.Listing}
          queryKey={ListingCatalogQueryKey}
          getCatalogDataExpanded={getCatalogDataExpandedCallback}
          onCatalogDataExpanded={onCatalogDataExpandedCallback}
          transformEventData={transformData}
        >
          <LoadingTracker componentName="InventoryEventsExplorer">
            <ListingRouteBody />
          </LoadingTracker>
        </CatalogDataContextProvider>
      </FilterQueryContextProvider>
    </StopwatchLoggingContextProvider>
  );
}

const ListingRouteBodyRaw = () => {
  const isMobile = useMatchMedia('mobile');
  const hasShowEventTagOnEventTileFeature = useUserHasFeature(
    Feature.ShowEventTagOnEventTile
  );
  const hasMinimalFiltersForMetricsFeature = useUserHasFeature(
    Feature.MinimalFiltersForMetrics_Inventory
  );
  const [filterQuery] =
    useFilterQueryContextFilterQuerySelector<ListingQuery>();
  const getActClientCfg = useGetActiveAccountClientConfig(
    'Inventory.ListingClient.getListingByListingId'
  );

  const getActivePosEntity = useCallback(
    async (
      posEntityId: number,
      curEntity?: ListingDetails | null,
      dataToGet?: string[] | null
    ) => {
      const activeClientCf = await getActClientCfg();
      if (activeClientCf?.activeAccountId == null) {
        return {};
      }

      const listingDetail = await new ListingClient(
        activeClientCf
      ).getListingByListingId(
        posEntityId,
        curEntity ?? undefined,
        dataToGet?.map((s) => s as ListingDetailDataField) ?? []
      );

      if (listingDetail) {
        return {
          posEntityId: listingDetail.id,
          posEntity: listingDetail,
          posEntityDisplayId: listingDetail.idOnMkp,
        };
      }

      return {};
    },
    [getActClientCfg]
  );

  const isSidePanelFlyover =
    filterQuery.viewMode === InventoryViewMode.MetricView;

  const defaultSorting = useMemo(() => {
    if (filterQuery.viewMode === InventoryViewMode.MetricView) {
      return [
        {
          id: EventsTableColumnId.Date,
          desc: false,
        },
      ];
    }
    return DefaultListingTableSorting;
  }, [filterQuery.viewMode]);

  const tableViewMode = useMemo(() => {
    if (filterQuery.viewMode === InventoryViewMode.MetricView) {
      return TableViewMode.InsightView;
    }
    if (filterQuery.viewMode === InventoryViewMode.FlattenedView) {
      return TableViewMode.SearchView;
    }
    return TableViewMode.EventView;
  }, [filterQuery.viewMode]);

  return (
    <CatalogMetricsContextProvider<ListingMetrics, ListingQuery>
      queryKey={
        isSidePanelFlyover
          ? EVENT_DETAILED_METRICS_QUERY_KEY
          : ListingCatalogMetricsQueryKey
      }
      entityType={ActionOutboxEntityType.Listing}
      isDetailed={isSidePanelFlyover}
      getCatalogMetrics={(c, f, r) =>
        isSidePanelFlyover
          ? getEventDetailedMetrics(c, f, r)
          : getCatalogMetrics(c, f, r, hasShowEventTagOnEventTileFeature)
      }
      addCatalogMetrics={addListingMetrics}
      additionalTransformQuery={
        hasMinimalFiltersForMetricsFeature
          ? minimizeListingQueryForMetrics
          : undefined
      }
    >
      <ActivePosEntityProvider<ListingDetails>
        entityType={ActionOutboxEntityType.Listing}
        getActivePosEntity={getActivePosEntity}
      >
        <ListingStatusContextProvider>
          <ListingNotificationContextProvider>
            <ActiveSortTableColumnProvider
              entityType={ActionOutboxEntityType.Listing}
              viewMode={tableViewMode}
              defaultSorting={defaultSorting}
            >
              <AdCampaignsDataContextProvider>
                {isMobile ? (
                  <InventoryEventsMobile />
                ) : (
                  <SidePanelProvider
                    sidePanelId="main-pages"
                    panelType={isSidePanelFlyover ? 'flyover' : 'content'}
                  >
                    <InventoryEventsExplorer />
                  </SidePanelProvider>
                )}
              </AdCampaignsDataContextProvider>
            </ActiveSortTableColumnProvider>
          </ListingNotificationContextProvider>{' '}
        </ListingStatusContextProvider>
      </ActivePosEntityProvider>
    </CatalogMetricsContextProvider>
  );
};

const ListingRouteBody = memo(ListingRouteBodyRaw);

export const Inventory = memo(InventoryRaw);
